/**
 * TBMuktBharat Profile Page - Optimized Version
 * Removed heavy animations for better performance
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import userService from '@/services/userService'
import { getApiErrorMessage } from '@/services/api'
import postService, { Post as SavedPost } from '@/services/postService'
import reelService, { Reel } from '@/services/reelService'
import type { User, Post } from '@/types'
import { getMediaUrl, getProfileImageUrl } from '@/utils/mediaUtils'
import GovLayout from '@/components/layout/GovLayout'
import {
  Heart, MessageCircle, Edit2, Settings, Camera, Loader2, X, UserPlus, UserMinus,
  Calendar, MapPin, Award, Clock, CheckCircle, Star, Home, Search, Bell, LogOut,
  BookOpen, Target, Users, FileText, Bookmark, Share2, Trash2, Repeat, Play, Maximize2
} from 'lucide-react'
import Lightbox from '@/components/Lightbox'

// ================================================================================
// EDIT MODAL - Simplified
// ================================================================================
const EditModal = ({ user, isOpen, onClose, onSave }: { user: User; isOpen: boolean; onClose: () => void; onSave: (data: Partial<User>) => Promise<void> }) => {
  const [formData, setFormData] = useState({
    firstName: user.firstName || '',
    lastName: user.lastName || '',
    bio: user.bio || '',
    phone: user.phone || '',
  })
  const [saving, setSaving] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    try {
      await onSave(formData)
      onClose()
    } catch (e) {
      console.error(e)
    }
    setSaving(false)
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-lg font-bold">Edit Profile</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <input
              type="text"
              value={formData.firstName}
              onChange={e => setFormData({ ...formData, firstName: e.target.value })}
              placeholder="First Name"
              className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm"
            />
            <input
              type="text"
              value={formData.lastName}
              onChange={e => setFormData({ ...formData, lastName: e.target.value })}
              placeholder="Last Name"
              className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm"
            />
          </div>
          <textarea
            value={formData.bio}
            onChange={e => setFormData({ ...formData, bio: e.target.value })}
            placeholder="Bio"
            rows={2}
            className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm resize-none"
          />
          <input
            type="tel"
            value={formData.phone}
            onChange={e => setFormData({ ...formData, phone: e.target.value })}
            placeholder="Phone"
            className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm"
          />
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2 border border-gray-200 rounded-lg text-sm font-medium">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium disabled:opacity-50">
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ================================================================================
// FOLLOWERS MODAL - Simplified
// ================================================================================
const FollowersModal = ({ userId, type, isOpen, onClose }: { userId: number; type: 'followers' | 'following'; isOpen: boolean; onClose: () => void }) => {
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setLoading(true)
      const load = async () => {
        try {
          const res = type === 'followers'
            ? await userService.getFollowers(userId, 0, 50)
            : await userService.getFollowing(userId, 0, 50)
          setUsers(res?.content || [])
        } catch (e) {
          console.error(e)
        }
        setLoading(false)
      }
      load()
    }
  }, [isOpen, userId, type])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-bold">{type === 'followers' ? 'Followers' : 'Following'}</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {loading ? (
            <div className="p-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-blue-500" /></div>
          ) : users.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No {type} yet</div>
          ) : (
            users.map(u => (
              <div
                key={u.id}
                className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer"
                onClick={() => { onClose(); window.location.href = `/profile/${u.username}` }}
              >
                <img src={getProfileImageUrl(u.profileImage)} alt="" className="w-10 h-10 rounded-full object-cover" loading="lazy" />
                <div>
                  <p className="font-medium text-sm">{u.fullName}</p>
                  <p className="text-xs text-gray-500">@{u.username}</p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}

// ================================================================================
// WIDGETS - Memoized
// ================================================================================
const StatsCard = React.memo(({ posts, followers, following, onFollowersClick, onFollowingClick }: {
  posts: number
  followers: number
  following: number
  onFollowersClick: () => void
  onFollowingClick: () => void
}) => (
  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
    <h3 className="font-bold text-gray-900 mb-3 text-sm">Activity</h3>
    <div className="grid grid-cols-3 gap-2">
      <div className="text-center p-3 bg-blue-50 rounded-lg">
        <div className="text-xl font-bold text-blue-600">{posts}</div>
        <div className="text-xs text-gray-500">Posts</div>
      </div>
      <button onClick={onFollowersClick} className="text-center p-3 bg-green-50 rounded-lg hover:bg-green-100">
        <div className="text-xl font-bold text-green-600">{followers}</div>
        <div className="text-xs text-gray-500">Followers</div>
      </button>
      <button onClick={onFollowingClick} className="text-center p-3 bg-purple-50 rounded-lg hover:bg-purple-100">
        <div className="text-xl font-bold text-purple-600">{following}</div>
        <div className="text-xs text-gray-500">Following</div>
      </button>
    </div>
  </div>
))

const AchievementsWidget = React.memo(() => {
  const achievements = useMemo(() => [
    { icon: '🏆', title: 'TB Warrior' },
    { icon: '💡', title: 'Knowledge Seeker' },
    { icon: '🤝', title: 'Community Helper' },
  ], [])

  return (
    <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
      <h3 className="font-bold text-gray-900 mb-3 text-sm flex items-center gap-2">
        <Award className="w-4 h-4 text-yellow-500" /> Badges
      </h3>
      <div className="flex gap-2">
        {achievements.map((a, i) => (
          <div key={i} className="flex-1 text-center p-2 bg-gradient-to-br from-yellow-50 to-amber-50 rounded-lg">
            <span className="text-2xl">{a.icon}</span>
            <p className="text-xs font-medium mt-1">{a.title}</p>
          </div>
        ))}
      </div>
    </div>
  )
})

const ActivityWidget = React.memo(() => {
  const activities = useMemo(() => [
    { icon: '📝', action: 'Posted about TB awareness', time: '2h' },
    { icon: '✅', action: 'Completed health quiz', time: '1d' },
    { icon: '👥', action: 'Joined TB Support Group', time: '2d' },
  ], [])

  return (
    <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
      <h3 className="font-bold text-gray-900 mb-3 text-sm flex items-center gap-2">
        <Clock className="w-4 h-4 text-green-500" /> Activity
      </h3>
      <div className="space-y-2">
        {activities.map((a, i) => (
          <div key={i} className="flex items-center gap-2 text-sm">
            <span>{a.icon}</span>
            <div className="flex-1">
              <p className="text-gray-900">{a.action}</p>
            </div>
            <span className="text-xs text-gray-400">{a.time}</span>
          </div>
        ))}
      </div>
    </div>
  )
})

// ================================================================================
// POST GRID - Optimized
// ================================================================================
const PostGrid = React.memo(({ posts, loading, canDelete, onDelete, resharedIds, onUnreshare, onOpenPost }: {
  posts: Post[]
  loading: boolean
  canDelete?: boolean
  onDelete?: (id: number) => void
  // ids of posts the profile owner reshared (reposted) — rendered with a
  // "Reposted" badge; on the owner's own profile these get an un-reshare
  // (remove) button instead of the delete button.
  resharedIds?: Set<number>
  onUnreshare?: (id: number) => void
  // Clicking a tile opens the single-post detail view (feed-style, all actions).
  onOpenPost?: (id: number) => void
}) => {
  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="aspect-square bg-gray-100 rounded-lg animate-pulse" />
        ))}
      </div>
    )
  }

  if (!posts?.length) {
    return (
      <div className="bg-white rounded-xl p-8 text-center shadow-sm border border-gray-100">
        <div className="text-4xl mb-3">📰</div>
        <p className="font-medium">No posts yet</p>
        <p className="text-sm text-gray-500">Start sharing your TB journey!</p>
      </div>
    )
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
      {posts.map((post) => {
        const isReshare = resharedIds?.has(post.id)
        return (
        <div
          key={post.id}
          onClick={() => onOpenPost?.(post.id)}
          className="aspect-square relative group cursor-pointer overflow-hidden rounded-lg"
        >
          {post.media?.[0]?.mediaUrl ? (
            <img src={getMediaUrl(post.media[0].mediaUrl)} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-blue-100 to-cyan-100 flex items-center justify-center p-3">
              <p className="text-xs text-center text-blue-700 line-clamp-3">{post.content}</p>
            </div>
          )}
          {isReshare && (
            <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-0.5 bg-green-600/90 text-white text-[10px] font-medium rounded-full shadow">
              <Repeat className="w-3 h-3" /> Reposted
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-between px-3 pb-3">
            <div className="flex items-center gap-3 text-white text-sm">
              <span className="flex items-center gap-1"><Heart className="w-4 h-4" /> {post.likeCount || 0}</span>
              <span className="flex items-center gap-1"><MessageCircle className="w-4 h-4" /> {post.commentCount || 0}</span>
            </div>
            {isReshare ? (
              onUnreshare && (
                <button
                  onClick={(e) => { e.stopPropagation(); onUnreshare(post.id) }}
                  title="Remove reshare (un-repost)"
                  className="p-1.5 bg-white/20 hover:bg-green-600 text-white rounded-full transition-colors"
                >
                  <Repeat className="w-4 h-4" />
                </button>
              )
            ) : (
              canDelete && onDelete && (
                <button
                  onClick={(e) => { e.stopPropagation(); onDelete(post.id) }}
                  title="Delete post"
                  className="p-1.5 bg-white/20 hover:bg-red-500 text-white rounded-full transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )
            )}
          </div>
        </div>
        )
      })}
    </div>
  )
})

// ================================================================================
// REEL GRID - profile Reels tab: authored reels + reshared (reposted) reels.
// ================================================================================
const ReelGrid = React.memo(({ reels, loading, navigate, resharedIds, onUnreshare }: {
  reels: Reel[]
  loading: boolean
  navigate: (path: string) => void
  resharedIds?: Set<number>
  onUnreshare?: (id: number) => void
}) => {
  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="aspect-[9/16] bg-gray-100 rounded-lg animate-pulse" />
        ))}
      </div>
    )
  }

  if (!reels?.length) {
    return (
      <div className="bg-white rounded-xl p-8 text-center shadow-sm border border-gray-100">
        <div className="text-4xl mb-3">🎞️</div>
        <p className="font-medium">No reels yet</p>
        <p className="text-sm text-gray-500">Reels you create or repost will appear here.</p>
      </div>
    )
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
      {reels.map((reel) => {
        const isReshare = resharedIds?.has(reel.id)
        const thumb = reel.thumbnailUrl
        return (
          <div
            key={reel.id}
            className="aspect-[9/16] relative group cursor-pointer overflow-hidden rounded-lg bg-black"
            onClick={() => navigate(`/reel/${reel.id}`)}
          >
            {thumb ? (
              <img src={getMediaUrl(thumb)} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
            ) : (
              <video src={getMediaUrl(reel.videoUrl)} className="w-full h-full object-cover" muted preload="metadata" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            {/* Play indicator */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-10 h-10 rounded-full bg-white/25 backdrop-blur flex items-center justify-center group-hover:scale-110 transition-transform">
                <Play className="w-5 h-5 text-white fill-white ml-0.5" />
              </div>
            </div>
            {isReshare && (
              <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-0.5 bg-green-600/90 text-white text-[10px] font-medium rounded-full shadow">
                <Repeat className="w-3 h-3" /> Reposted
              </div>
            )}
            <div className="absolute bottom-2 left-2 right-2 flex items-end justify-between">
              <div className="flex items-center gap-2 text-white text-xs">
                <span className="flex items-center gap-1"><Heart className="w-3.5 h-3.5" /> {reel.likeCount || 0}</span>
                <span className="flex items-center gap-1"><MessageCircle className="w-3.5 h-3.5" /> {reel.commentCount || 0}</span>
                <span className="flex items-center gap-1"><Play className="w-3.5 h-3.5" /> {reel.viewCount || 0}</span>
              </div>
              {isReshare && onUnreshare && (
                <button
                  onClick={(e) => { e.stopPropagation(); onUnreshare(reel.id) }}
                  title="Remove reshare (un-repost)"
                  className="p-1.5 bg-white/20 hover:bg-green-600 text-white rounded-full transition-colors"
                >
                  <Repeat className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
})

// ================================================================================
// SAVED POST CARD - interactive (image/video playback, like, comment, share, unsave)
// Self-contained so the profile Saved tab behaves like the feed without touching FeedComponents.
// ================================================================================
const SavedPostCard = React.memo(({ post, onUnsave, onOpenComments, onOpenPost }: {
  post: SavedPost
  onUnsave: (id: number) => void
  onOpenComments: (id: number) => void
  // Clicking the post media/content opens the single-post detail view
  // (feed-style, with ALL actions incl. reshare + delete). The quick-action
  // footer buttons (like/comment/share/unsave) stay independent.
  onOpenPost?: (id: number) => void
}) => {
  const [isLiked, setIsLiked] = useState(post.isLiked || false)
  const [likes, setLikes] = useState(post.likeCount || 0)
  // Full-media lightbox — opens when the saved post's image (or expand button)
  // is clicked, to view the uncropped media. Clicking the post text still opens
  // the single-post detail view (onOpenPost).
  const [showLightbox, setShowLightbox] = useState(false)

  const firstMedia = post.media?.[0]
  const hasMedia = firstMedia?.mediaUrl

  const formatTime = useCallback((date?: string) => {
    if (!date) return ''
    const diff = Math.floor((Date.now() - new Date(date).getTime()) / 1000)
    if (diff < 60) return 'Just now'
    if (diff < 3600) return `${Math.floor(diff / 60)}m`
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`
    return new Date(date).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })
  }, [])

  const handleLike = async () => {
    const next = !isLiked
    setIsLiked(next)
    setLikes(prev => next ? prev + 1 : Math.max(0, prev - 1))
    try {
      await postService.react(post.id, 'LIKE')
    } catch (e) {
      setIsLiked(!next)
      setLikes(prev => next ? Math.max(0, prev - 1) : prev + 1)
      console.error('Like failed:', e)
    }
  }

  const handleShare = () => {
    const url = `${window.location.origin}/post/${post.id}`
    if (navigator.share) {
      navigator.share({ title: 'Check out this post', text: post.content?.substring(0, 100) || 'TB Mukt Bharat Post', url }).catch(() => {
        navigator.clipboard.writeText(url)
        alert('Link copied to clipboard!')
      })
    } else {
      navigator.clipboard.writeText(url)
      alert('Link copied to clipboard!')
    }
  }

  return (
    <article className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100">
      {hasMedia && (
        <div className="relative bg-gray-50 group">
          {firstMedia?.mediaType === 'VIDEO' ? (
            <div className="aspect-video">
              <video src={getMediaUrl(firstMedia.mediaUrl)} controls className="w-full h-full object-contain" onClick={(e) => e.stopPropagation()} />
            </div>
          ) : (
            <div
              className="max-h-96 overflow-hidden cursor-zoom-in"
              onClick={() => setShowLightbox(true)}
            >
              <img
                src={getMediaUrl(firstMedia.mediaUrl)}
                alt=""
                className="w-full h-auto object-contain max-h-96"
                loading="lazy"
              />
            </div>
          )}
          {/* Expand button — opens the full media in a lightbox. For images the
              image itself is also clickable; for videos this avoids touching the
              inline controls. */}
          <button
            onClick={() => setShowLightbox(true)}
            title="View full"
            aria-label="View full media"
            className="absolute top-2 right-2 p-2 bg-black/40 hover:bg-black/60 text-white rounded-full transition-colors"
          >
            <Maximize2 className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="p-4">
        <div className="flex items-center gap-3 mb-3">
          <img src={getProfileImageUrl(post.user?.profileImage)} alt="" className="w-9 h-9 rounded-full object-cover" loading="lazy" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-900 text-sm truncate">{post.user?.firstName} {post.user?.lastName}</p>
            <p className="text-xs text-gray-500">{formatTime(post.createdAt)}</p>
          </div>
          <button onClick={() => onUnsave(post.id)} title="Remove from saved" className="p-1.5 hover:bg-gray-100 rounded-full">
            <Bookmark className="w-4 h-4 fill-yellow-500 text-yellow-500" />
          </button>
        </div>

        {post.content && (
          <p
            className="text-gray-800 text-sm mb-3 line-clamp-3 cursor-pointer hover:text-gray-900"
            onClick={() => onOpenPost?.(post.id)}
          >
            {post.content}
          </p>
        )}

        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <div className="flex items-center gap-3">
            <button onClick={handleLike} className={`flex items-center gap-1 ${isLiked ? 'text-red-500' : 'text-gray-400 hover:text-red-500'}`}>
              <Heart className={`w-4 h-4 ${isLiked && 'fill-current'}`} />
              <span className="text-xs">{likes > 0 && likes}</span>
            </button>
            <button onClick={() => onOpenComments(post.id)} className="flex items-center gap-1 text-gray-400 hover:text-blue-500">
              <MessageCircle className="w-4 h-4" />
              <span className="text-xs">{(post.commentCount || 0) > 0 && post.commentCount}</span>
            </button>
            <button onClick={handleShare} className="text-gray-400 hover:text-green-500"><Share2 className="w-4 h-4" /></button>
          </div>
        </div>
      </div>

      {/* Full-media lightbox — fixed, escapes the card's overflow-hidden. */}
      {showLightbox && hasMedia && (
        <Lightbox
          src={getMediaUrl(firstMedia!.mediaUrl)}
          mediaType={firstMedia?.mediaType}
          onClose={() => setShowLightbox(false)}
        />
      )}
    </article>
  )
})
SavedPostCard.displayName = 'SavedPostCard'

// ================================================================================
// MAIN COMPONENT
// ================================================================================
const ProfilePage = () => {
  const { username } = useParams()
  const navigate = useNavigate()
  const { user: currentUser, setUser, logout } = useAuthStore()

  const [profileUser, setProfileUser] = useState<User | null>(null)
  const [posts, setPosts] = useState<Post[]>([])
  // Posts the profile owner has reshared (reposted from others). Merged into
  // the Posts tab alongside their authored posts (Instagram-style), each shown
  // with a "Reposted" badge + an un-reshare action on the owner's own profile.
  const [resharedPosts, setResharedPosts] = useState<Post[]>([])
  // Reels tab: authored reels + reshared reels, same treatment as posts.
  const [reels, setReels] = useState<Reel[]>([])
  const [resharedReels, setResharedReels] = useState<Reel[]>([])
  const [reelsLoading, setReelsLoading] = useState(false)
  const [stats, setStats] = useState({ posts: 0, followers: 0, following: 0 })
  const [loading, setLoading] = useState(true)
  const [isFollowing, setIsFollowing] = useState(false)
  const [followLoading, setFollowLoading] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [followersOpen, setFollowersOpen] = useState(false)
  const [followingOpen, setFollowingOpen] = useState(false)
  const [activeTab, setActiveTab] = useState('posts')
  const [savedPosts, setSavedPosts] = useState<SavedPost[]>([])
  const [savedLoading, setSavedLoading] = useState(false)
  const [commentPostId, setCommentPostId] = useState<number | null>(null)
  const [commentText, setCommentText] = useState('')
  const [comments, setComments] = useState<any[]>([])
  const [loadingComments, setLoadingComments] = useState(false)
  const [submittingComment, setSubmittingComment] = useState(false)
  const profileInputRef = useRef<HTMLInputElement>(null)
  const coverInputRef = useRef<HTMLInputElement>(null)
  const [uploadingProfile, setUploadingProfile] = useState(false)
  const [uploadingCover, setUploadingCover] = useState(false)

  const isOwnProfile = !username || username === currentUser?.username

  // Load profile once on mount or when username changes
  useEffect(() => {
    let mounted = true
    const loadProfile = async () => {
      setLoading(true)
      setReelsLoading(true)
      try {
        let userData: any
        if (isOwnProfile) {
          // OPTIMIZED: Single API call for own profile with stats
          userData = await userService.getCurrentUserProfile()
        } else if (username) {
          // OPTIMIZED: Single API call for other user profiles
          userData = await userService.getUserProfile(username)
        }
        if (mounted && userData) {
          setProfileUser(userData)
          setStats({
            posts: userData.stats?.postsCount || 0,
            followers: userData.stats?.followersCount || 0,
            following: userData.stats?.followingCount || 0
          })
          setIsFollowing(userData.isFollowing || false)

          // Fetch authored posts + reshared (reposted) posts + authored reels +
          // reshared reels in parallel. The reshare endpoints are public
          // (GET /posts|/reels/** permitAll), so they work for both the owner's
          // own profile and for viewing other users' profiles. Reshared items
          // carry resharedAt + the ORIGINAL author in post.user/reel.user —
          // the post/reel itself is never duplicated (Instagram/retweet style).
          const profileUserId = userData.id
          const [postsRes, resharedRes, reelsRes, resharedReelsRes] = await Promise.all([
            userService.getUserPosts(profileUserId, 0, 12).catch(() => null),
            postService.getResharedPosts(profileUserId, 0, 20).catch(() => null),
            reelService.getUserReels(profileUserId, 0, 12).catch(() => null),
            reelService.getResharedReels(profileUserId, 0, 20).catch(() => null),
          ])
          if (!mounted) return
          // userService.getUserPosts + reelService.* return the Page directly
          // (response.data.data) → .content. postService.getResharedPosts returns
          // the ApiResponse wrapper (response.data) → the page lives at .data.content.
          setPosts(postsRes?.content || [])
          setResharedPosts(resharedRes?.data?.content || [])
          setReels(reelsRes?.content || [])
          setResharedReels(resharedReelsRes?.content || [])
        }
      } catch (e) {
        console.error(e)
      }
      if (mounted) setLoading(false)
      if (mounted) setReelsLoading(false)
    }
    loadProfile()
    return () => { mounted = false }
  }, [username]) // Only depend on username

  // Merge authored + reshared (reposted) posts/reels for the grid. Reshared
  // items carry resharedAt; authored items use createdAt. Sort newest-activity
  // first so a recent repost surfaces above older authored posts (Strishakti-
  // style profile merge — the original post is never duplicated, only the
  // profile owner's reshare row is).
  const mergedPosts = useMemo(() => {
    return [...posts, ...resharedPosts].sort((a, b) => {
      const ta = new Date(a.resharedAt || a.createdAt).getTime()
      const tb = new Date(b.resharedAt || b.createdAt).getTime()
      return tb - ta
    })
  }, [posts, resharedPosts])
  const resharedPostIds = useMemo(() => new Set(resharedPosts.map(p => p.id)), [resharedPosts])

  const mergedReels = useMemo(() => {
    return [...reels, ...resharedReels].sort((a, b) => {
      const ta = new Date(a.resharedAt || a.createdAt).getTime()
      const tb = new Date(b.resharedAt || b.createdAt).getTime()
      return tb - ta
    })
  }, [reels, resharedReels])
  const resharedReelIds = useMemo(() => new Set(resharedReels.map(r => r.id)), [resharedReels])

  // Fetch saved posts for the Saved tab (own profile only — saved posts are private)
  const fetchSavedPosts = useCallback(async () => {
    try {
      setSavedLoading(true)
      const response = await postService.getSavedPosts(0, 12)
      if (response?.success) {
        setSavedPosts(response.data?.content || [])
      }
    } catch (error) {
      console.error('Error fetching saved posts:', error)
    } finally {
      setSavedLoading(false)
    }
  }, [])

  useEffect(() => {
    if (isOwnProfile && activeTab === 'saved') {
      fetchSavedPosts()
    }
  }, [isOwnProfile, activeTab, fetchSavedPosts])

  // Open the comments modal for a saved post (fetch latest comments)
  const handleOpenComments = useCallback(async (postId: number) => {
    setCommentPostId(postId)
    setLoadingComments(true)
    setComments([])
    try {
      const res = await postService.getComments(postId, 0, 20)
      setComments(res.data?.content || res.data || [])
    } catch (e) {
      console.error('Error fetching comments:', e)
    }
    setLoadingComments(false)
  }, [])

  // Submit a comment and refresh the list + bump the count on the card
  const handleSubmitComment = useCallback(async () => {
    if (!commentPostId || !commentText.trim()) return
    setSubmittingComment(true)
    try {
      await postService.addComment(commentPostId, commentText.trim())
      setCommentText('')
      const res = await postService.getComments(commentPostId, 0, 20)
      setComments(res.data?.content || res.data || [])
      setSavedPosts(prev => prev.map(p =>
        p.id === commentPostId ? { ...p, commentCount: (p.commentCount || 0) + 1 } : p
      ))
    } catch (e) {
      console.error('Error adding comment:', e)
    }
    setSubmittingComment(false)
  }, [commentPostId, commentText])

  // Unsave (bookmark toggle) and remove from the saved list optimistically
  const handleUnsave = useCallback(async (postId: number) => {
    setSavedPosts(prev => prev.filter(p => p.id !== postId))
    try {
      await postService.toggleSave(postId)
    } catch (e) {
      console.error('Unsave failed, refetching:', e)
      fetchSavedPosts()
    }
  }, [fetchSavedPosts])

  // Delete own post (profile grid)
  const handleDeletePost = useCallback(async (postId: number) => {
    if (!window.confirm('Delete this post permanently? This cannot be undone.')) return
    setPosts(prev => prev.filter(p => p.id !== postId))
    setStats(prev => ({ ...prev, posts: Math.max(0, prev.posts - 1) }))
    try {
      await postService.deletePost(postId)
    } catch (e) {
      console.error('Delete post failed, refetching:', e)
      alert('Could not delete the post. Please try again.')
      // Refetch to restore the grid
      if (profileUser) {
        userService.getUserPosts(profileUser.id, 0, 12).then(d => setPosts(d?.content || []))
      }
    }
  }, [profileUser])

  // Remove the profile owner's reshare of a post (un-repost). Only that user's
  // reshare row is deleted — the original post and other people's reshares are
  // untouched (Instagram/retweet semantics). Optimistic: drop it from
  // resharedPosts so it leaves the grid immediately.
  const handleUnresharePost = useCallback(async (postId: number) => {
    setResharedPosts(prev => prev.filter(p => p.id !== postId))
    try {
      await postService.unresharePost(postId)
    } catch (e) {
      console.error('Un-reshare post failed, refetching:', e)
      if (profileUser) {
        postService.getResharedPosts(profileUser.id, 0, 20)
          .then(res => setResharedPosts(res?.data?.content || []))
      }
    }
  }, [profileUser])

  // Same as above for reels.
  const handleUnreshareReel = useCallback(async (reelId: number) => {
    setResharedReels(prev => prev.filter(r => r.id !== reelId))
    try {
      await reelService.unreshare(reelId)
    } catch (e) {
      console.error('Un-reshare reel failed, refetching:', e)
      if (profileUser) {
        reelService.getResharedReels(profileUser.id, 0, 20)
          .then(res => setResharedReels(res?.content || []))
      }
    }
  }, [profileUser])

  // Open the single-post detail view (feed-style, all actions) when a profile
  // grid tile or a saved post's media/content is clicked. Reels use the
  // ReelGrid's navigate prop directly → /reel/:id.
  const handleOpenPost = useCallback((postId: number) => {
    navigate(`/post/${postId}`)
  }, [navigate])

  const handleFollow = useCallback(async () => {
    if (!profileUser) return
    setFollowLoading(true)
    try {
      if (isFollowing) {
        await userService.unfollowUser(profileUser.id)
        setIsFollowing(false)
        setStats(prev => ({ ...prev, followers: prev.followers - 1 }))
      } else {
        await userService.followUser(profileUser.id)
        setIsFollowing(true)
        setStats(prev => ({ ...prev, followers: prev.followers + 1 }))
      }
    } catch (e) {
      console.error(e)
    }
    setFollowLoading(false)
  }, [profileUser, isFollowing])

  const handleProfileUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !isOwnProfile || !currentUser) return
    if (file.size > 20 * 1024 * 1024) {
      const mb = (file.size / (1024 * 1024)).toFixed(1)
      alert(`This image is ${mb} MB. The maximum allowed upload is 20 MB. Please choose a smaller image.`)
      e.target.value = ''
      return
    }
    setUploadingProfile(true)
    try {
      const res = await userService.uploadProfileImage(file)
      const updated = { ...currentUser, profileImage: res.url } as User
      setUser(updated)
      setProfileUser(updated)
    } catch (err) {
      console.error(err)
      alert(getApiErrorMessage(err, 'Failed to upload profile image. Please try again.'))
    }
    setUploadingProfile(false)
  }, [currentUser, isOwnProfile, setUser])

  const handleCoverUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !isOwnProfile || !currentUser) return
    if (file.size > 20 * 1024 * 1024) {
      const mb = (file.size / (1024 * 1024)).toFixed(1)
      alert(`This image is ${mb} MB. The maximum allowed upload is 20 MB. Please choose a smaller image.`)
      e.target.value = ''
      return
    }
    setUploadingCover(true)
    try {
      const res = await userService.uploadCoverImage(file)
      const updated = { ...currentUser, coverImage: res.url } as User
      setUser(updated)
      setProfileUser(updated)
    } catch (err) {
      console.error(err)
      alert(getApiErrorMessage(err, 'Failed to upload cover image. Please try again.'))
    }
    setUploadingCover(false)
  }, [currentUser, isOwnProfile, setUser])

  const handleEdit = useCallback(async (data: Partial<User>) => {
    const updated = await userService.updateProfile(data)
    setUser(updated)
    setProfileUser(updated)
  }, [setUser])

  if (loading) {
    return (
      <GovLayout>
        <div className="animate-pulse space-y-4">
          <div className="h-40 bg-gray-200 rounded-xl" />
          <div className="h-32 bg-gray-200 rounded-xl" />
        </div>
      </GovLayout>
    )
  }

  if (!profileUser) {
    return (
      <GovLayout>
        <div className="text-center py-12">
          <div className="text-4xl mb-3">👤</div>
          <p className="font-medium">User not found</p>
          <button onClick={() => navigate('/feed')} className="text-blue-600 text-sm mt-2">Go to Feed</button>
        </div>
      </GovLayout>
    )
  }

  return (
    <GovLayout>
        {/* Cover & Profile */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-4">
          {/* Cover */}
          <div className="relative h-36 md:h-48 bg-gradient-to-r from-blue-500 via-cyan-500 to-teal-400">
            {profileUser.coverImage && (
              <img src={profileUser.coverImage} alt="" className="w-full h-full object-cover" loading="lazy" />
            )}
            {isOwnProfile && (
              <button
                onClick={() => coverInputRef.current?.click()}
                className="absolute bottom-3 right-3 p-2 bg-white/90 hover:bg-white rounded-lg shadow text-sm"
                disabled={uploadingCover}
              >
                {uploadingCover ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
              </button>
            )}
            <input ref={coverInputRef} type="file" accept="image/*" className="hidden" onChange={handleCoverUpload} />
          </div>

          {/* Info */}
          <div className="px-4 pb-4">
            <div className="flex flex-col md:flex-row gap-4 -mt-12 md:-mt-10">
              {/* Avatar */}
              <div className="relative flex-shrink-0">
                <div className="w-24 h-24 md:w-28 md:h-28 rounded-xl border-4 border-white shadow-lg overflow-hidden bg-gray-100">
                  <img src={getProfileImageUrl(profileUser.profileImage)} alt="" className="w-full h-full object-cover" loading="lazy" />
                </div>
                {isOwnProfile && (
                  <button
                    onClick={() => profileInputRef.current?.click()}
                    className="absolute bottom-1 right-1 p-1.5 bg-blue-500 text-white rounded-full shadow"
                    disabled={uploadingProfile}
                  >
                    {uploadingProfile ? <Loader2 className="w-3 h-3 animate-spin" /> : <Camera className="w-3 h-3" />}
                  </button>
                )}
                <input ref={profileInputRef} type="file" accept="image/*" className="hidden" onChange={handleProfileUpload} />
              </div>

              {/* Details */}
              <div className="flex-1 pt-2 md:pt-12">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h1 className="text-xl font-bold">{profileUser.fullName}</h1>
                      {profileUser.emailVerified && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                          <CheckCircle className="w-3 h-3" /> Verified
                        </span>
                      )}
                    </div>
                    <p className="text-gray-500 text-sm">@{profileUser.username}</p>
                    {profileUser.bio && <p className="text-gray-700 text-sm mt-1">{profileUser.bio}</p>}
                  </div>

                  <div className="flex gap-2">
                    {isOwnProfile ? (
                      <>
                        <button onClick={() => setEditOpen(true)} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 flex items-center gap-1.5">
                          <Edit2 className="w-4 h-4" /> Edit
                        </button>
                        <button onClick={() => navigate('/settings')} className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50">
                          <Settings className="w-4 h-4 text-gray-600" />
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={handleFollow}
                        disabled={followLoading}
                        className={`px-4 py-2 text-sm font-medium rounded-lg flex items-center gap-1.5 ${
                          isFollowing
                            ? 'border border-gray-200 text-gray-700 hover:bg-red-50 hover:text-red-500'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                      >
                        {followLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : isFollowing ? <><UserMinus className="w-4 h-4" /> Following</> : <><UserPlus className="w-4 h-4" /> Follow</>}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* Sidebar */}
          <div className="lg:col-span-4 space-y-4">
            <StatsCard
              posts={stats.posts}
              followers={stats.followers}
              following={stats.following}
              onFollowersClick={() => setFollowersOpen(true)}
              onFollowingClick={() => setFollowingOpen(true)}
            />
            <AchievementsWidget />
            <ActivityWidget />
          </div>

          {/* Content */}
          <div className="lg:col-span-8 space-y-4">
            {/* Tabs */}
            <div className="bg-white rounded-xl p-1.5 shadow-sm border border-gray-100">
              <div className="flex gap-1">
                {[
                  { id: 'posts', label: 'Posts', icon: FileText },
                  { id: 'reels', label: 'Reels', icon: Play },
                  { id: 'saved', label: 'Saved', icon: Star },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-medium transition-colors ${
                      activeTab === tab.id
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    <tab.icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Posts — authored + reshared (reposted), merged newest-activity first */}
            {activeTab === 'posts' && (
              <PostGrid
                posts={mergedPosts}
                loading={false}
                canDelete={isOwnProfile}
                onDelete={handleDeletePost}
                resharedIds={resharedPostIds}
                onUnreshare={isOwnProfile ? handleUnresharePost : undefined}
                onOpenPost={handleOpenPost}
              />
            )}
            {/* Reels — authored + reshared (reposted), merged newest-activity first */}
            {activeTab === 'reels' && (
              <ReelGrid
                reels={mergedReels}
                loading={reelsLoading}
                navigate={navigate}
                resharedIds={resharedReelIds}
                onUnreshare={isOwnProfile ? handleUnreshareReel : undefined}
              />
            )}
            {activeTab === 'saved' && (
              isOwnProfile ? (
                savedLoading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[...Array(4)].map((_, i) => (
                      <div key={i} className="bg-white rounded-xl p-4 animate-pulse h-48" />
                    ))}
                  </div>
                ) : savedPosts.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {savedPosts.map((post) => (
                      <SavedPostCard
                        key={post.id}
                        post={post}
                        onUnsave={handleUnsave}
                        onOpenComments={handleOpenComments}
                        onOpenPost={handleOpenPost}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="bg-white rounded-xl p-8 text-center shadow-sm border border-gray-100">
                    <div className="text-4xl mb-3">📌</div>
                    <p className="font-medium">No saved posts yet</p>
                    <p className="text-sm text-gray-500">Posts you save will appear here</p>
                  </div>
                )
              ) : (
                <div className="bg-white rounded-xl p-8 text-center shadow-sm border border-gray-100">
                  <div className="text-4xl mb-3">📌</div>
                  <p className="font-medium">No saved posts yet</p>
                </div>
              )
            )}
          </div>
        </div>

      {/* Modals */}
      <EditModal user={profileUser} isOpen={editOpen} onClose={() => setEditOpen(false)} onSave={handleEdit} />
      <FollowersModal userId={profileUser.id} type="followers" isOpen={followersOpen} onClose={() => setFollowersOpen(false)} />
      <FollowersModal userId={profileUser.id} type="following" isOpen={followingOpen} onClose={() => setFollowingOpen(false)} />

      {/* Comments modal (opened from a saved post) */}
      {commentPostId && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4"
          onClick={() => setCommentPostId(null)}
        >
          <div
            className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[80vh] overflow-hidden shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-lg font-bold">Comments</h2>
              <button onClick={() => setCommentPostId(null)} className="p-1.5 hover:bg-gray-100 rounded-full">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 max-h-96 overflow-y-auto">
              {loadingComments ? (
                <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin mx-auto text-blue-600" /></div>
              ) : comments.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <MessageCircle className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>No comments yet</p>
                  <p className="text-sm">Be the first to comment!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {comments.map((comment: any) => (
                    <div key={comment.id} className="flex gap-3">
                      <img src={getProfileImageUrl(comment.user?.profileImage)} alt="" className="w-8 h-8 rounded-full object-cover" />
                      <div className="flex-1">
                        <div className="bg-gray-100 rounded-lg px-3 py-2">
                          <p className="font-medium text-sm">{comment.user?.firstName} {comment.user?.lastName}</p>
                          <p className="text-sm text-gray-700">{comment.content}</p>
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                          <span>{comment.createdAt ? new Date(comment.createdAt).toLocaleDateString() : ''}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="p-4 border-t border-gray-100 flex gap-3">
              <img src={getProfileImageUrl(currentUser?.profileImage)} alt="" className="w-8 h-8 rounded-full object-cover" />
              <div className="flex-1 flex gap-2">
                <input
                  type="text"
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder="Write a comment..."
                  className="flex-1 px-4 py-2 bg-gray-100 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  onKeyDown={(e) => e.key === 'Enter' && handleSubmitComment()}
                />
                <button
                  onClick={handleSubmitComment}
                  disabled={!commentText.trim() || submittingComment}
                  className="px-4 py-2 bg-blue-600 text-white rounded-full text-sm font-medium disabled:opacity-50"
                >
                  {submittingComment ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Post'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </GovLayout>
  )
}

export default ProfilePage